import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import { useState } from 'react';
import CurrencyFormat from 'react-currency-format';
import InputMask from 'react-input-mask';
import { ThemedComponent } from 'ui/theme';
import { MaterialInput } from './styled';

export const InputRaw = (props) => {
  const [visible, setVisible] = useState(false);
  const handleClickShowPassword = () => setVisible(!visible);
  const handleMouseDown = (event) => event.preventDefault();

  const renderInputContent = () => (
    <ThemedComponent>
      <FormControl fullWidth variant="standard">
        {props?.holder ? null : (
          <InputLabel
            style={{ fontSize: '20px', marginTop: '-4px' }}
            htmlFor={props.id}
            shrink
            color={props.secondary ? 'secondary' : 'primary'}
            required={props.required}
            focused={props.readOnly}
          >
            {props.placeholder}
          </InputLabel>
        )}
        <MaterialInput
          id={props.id}
          type={
            props.type === 'password'
              ? visible
                ? 'text'
                : 'password'
              : props.type
          }
          value={props.value}
          onChange={props.onChange}
          onKeyDown={(ev) =>
            typeof props.onSubmitEditing === 'function'
              ? ev.keyCode === 13
                ? props.onSubmitEditing()
                : null
              : props.onKeyDown
          }
          disabled={props.disabled}
          readOnly={props.readOnly}
          transparent={props.transparent}
          disableUnderline
          required={props.required}
          width={props.width}
          small={props.small}
          height={props.height}
          multiline={props.multiline}
          maxRows={props.maxRows}
          placeholder={props.holder ? props.placeholder : ''}
          style={props.style}
          color={props.secondary ? 'secondary' : 'primary'}
          min="0"
          endAdornment={
            props.type === 'password' || props.endIcon ? (
              <InputAdornment position="end">
                {props.endIcon && <IconButton>{props.endIcon}</IconButton>}
                {props.type === 'password' && (
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDown}
                  >
                    {visible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )}
              </InputAdornment>
            ) : null
          }
        />
      </FormControl>
    </ThemedComponent>
  );

  // Renderiza com Tooltip apenas se houver texto
  if (props.tooltipText) {
    return (
      <Tooltip title={props.tooltipText} arrow placement="top">
        <span>{renderInputContent()}</span>
      </Tooltip>
    );
  }

  return renderInputContent();
};

export const MaskedInput = (props) => {
  return (
    <InputMask
      mask={props.mask}
      value={props.value}
      disabled={props.disabled}
      placeholder={props.placeholder}
      color={props.color}
      transparent={props.transparent}
      onChange={props.onChange}
      maskChar=""
      required={props.required}
    >
      {(inputProps) => {
        const inputType = inputProps.type ?? 'text';
        return (
          <FormControl fullWidth variant="standard">
            {inputProps?.holder ? null : (
              <InputLabel
                style={{ fontSize: '20px', marginTop: '-4px' }}
                htmlFor={inputProps.id}
                shrink
                color={inputProps.secondary ? 'secondary' : 'primary'}
                required={inputProps.required}
              >
                {inputProps.placeholder}
              </InputLabel>
            )}
            <MaterialInput
              {...inputProps}
              type={inputType}
              onKeyDown={(ev) =>
                typeof inputProps.onSubmitEditing === 'function'
                  ? ev.keyCode === 13
                    ? inputProps.onSubmitEditing()
                    : null
                  : inputProps.onKeyDown
              }
              disableUnderline
              placeholder={inputProps.holder ? inputProps.placeholder : ''}
              color={inputProps.secondary ? 'secondary' : 'primary'}
              min="0"
            />
          </FormControl>
        );
      }}
    </InputMask>
  );
};

// --- CurrencyInput Component ---
export const CurrencyInput = ({ value, onChange, ...props }) => (
  <CurrencyFormat
    {...props}
    value={value}
    onValueChange={(values) => {
      const floatValue = values.floatValue;
      //caso campo seja undefinied ou NAN
      onChange({
        target: {
          value:
            floatValue === undefined || isNaN(floatValue) ? '' : floatValue,
        },
      });
    }}
    thousandSeparator="."
    decimalSeparator=","
    prefix="R$ "
    decimalScale={2}
    fixedDecimalScale
    customInput={Input}
    readOnly={props.readOnly}
    style={props.style}
  />
);

const defaultInputTypes = [
  'password',
  'text',
  'date',
  'money',
  'number',
  'email',
  'cpf',
];

// --- MaskedInput PropTypes e DefaultProps ---
MaskedInput.propTypes = {
  type: PropTypes.oneOf(defaultInputTypes),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string.isRequired,
  secondary: PropTypes.bool,
  onSubmitEditing: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  mask: PropTypes.string.isRequired,
  tooltipText: PropTypes.string,
  transparent: PropTypes.bool,
};

MaskedInput.defaultProps = {
  type: 'text',
  label: '',
  placeholder: '',
  value: '',
  secondary: false,
  onSubmitEditing: undefined,
  onChange: undefined,
  mask: '',
  tooltipText: undefined,
  transparent: true,
};

// --- Input (Default Export) Component ---
export default function Input(p) {
  return <InputRaw {...p} />;
}

// --- Input PropTypes e DefaultProps ---
Input.propTypes = {
  type: PropTypes.oneOf(defaultInputTypes),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string.isRequired,
  secondary: PropTypes.bool,
  disabled: PropTypes.bool,
  onSubmitEditing: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  transparent: PropTypes.bool,
  readOnly: PropTypes.bool,
  // tooltipText: PropTypes.string.isRequired,
};

Input.defaultProps = {
  type: 'text',
  label: '',
  placeholder: '',
  value: '',
  secondary: false,
  disabled: false,
  onSubmitEditing: undefined,
  onChange: undefined,
  tooltipText: undefined,
  transparent: true,
  readOnly: false,
};
